import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { NotFound } from '@/layouts/NotFound';
import { LoginPage } from '@/features/auth/LoginPage';
import { AuthCallback } from '@/features/auth/AuthCallback';
import { DashboardHomePage } from '@/features/dashboard/DashboardHomePage';
import { ComponentsShowcase } from '@/features/dev/ComponentsShowcase';
import { HealthDashboardPage } from '@/features/notifications/pages/HealthDashboardPage';
import { ConfigEditorPage } from '@/features/notifications/pages/ConfigEditorPage';
import { JourneysPage } from '@/features/notifications/pages/JourneysPage';
import { JourneyDetailPage } from '@/features/notifications/pages/JourneyDetailPage';
import { UserInspectorPage } from '@/features/notifications/pages/UserInspectorPage';
import { AlertsPoliciesPage } from '@/features/notifications/pages/AlertsPoliciesPage';
import { AlertsFiringsPage } from '@/features/notifications/pages/AlertsFiringsPage';
import { FiringDetailPage } from '@/features/notifications/pages/FiringDetailPage';
import { DecisionDetailPage } from '@/features/notifications/pages/DecisionDetailPage';
import { AuditPage } from '@/features/notifications/pages/AuditPage';
import { UsersListPage } from '@/features/users/pages/UsersListPage';
import { UserDetailPage } from '@/features/users/pages/UserDetailPage';
import { TeamsListPage } from '@/features/teams/pages/TeamsListPage';
import { TeamDetailPage } from '@/features/teams/pages/TeamDetailPage';
import { MatchingQueuePage } from '@/features/teams/pages/MatchingQueuePage';
import { SupportListPage } from '@/features/support/pages/SupportListPage';
import { SupportDetailPage } from '@/features/support/pages/SupportDetailPage';
import {
  CommunicationHomePage,
  CampaignDetailPage,
} from '@/features/communication/pages/CommunicationHomePage';
import { CampaignNewPage } from '@/features/communication/pages/CampaignNewPage';
import { AiHomePage, AiPromptDetailPage } from '@/features/ai/pages/AiHomePage';
import { CoverageGapsPage } from '@/features/ai/pages/CoverageGapsPage';
import { CoverageGapDetailPage } from '@/features/ai/pages/CoverageGapDetailPage';
import { ApproachOutcomesPage } from '@/features/ai/pages/ApproachOutcomesPage';
import { PortfolioStatePage } from '@/features/ai/pages/PortfolioStatePage';
import { UserModelsPage } from '@/features/ai/pages/UserModelsPage';
import {
  TaxonomyListPage,
  TaxonomyDetailPage,
} from '@/features/taxonomy/pages/TaxonomyHomePage';
import { StoreHomePage } from '@/features/store/pages/StoreHomePage';
import { AnalyticsPage } from '@/features/analytics/pages/AnalyticsPage';
import { SystemPage } from '@/features/system/pages/SystemPage';
import { MigrationsPage } from '@/features/migrations/pages/MigrationsPage';

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<AuthCallback />} />
      </Route>

      <Route element={<AppLayout />}>
        <Route path="/dashboard" element={<DashboardHomePage />} />

        {/* Slice B — Notifications */}
        <Route path="/notifications" element={<Navigate to="/notifications/dashboard" replace />} />
        <Route path="/notifications/dashboard" element={<HealthDashboardPage />} />
        <Route path="/notifications/config" element={<ConfigEditorPage />} />
        <Route path="/notifications/journeys" element={<JourneysPage />} />
        <Route path="/notifications/journeys/:id" element={<JourneyDetailPage />} />
        <Route path="/notifications/users" element={<UserInspectorPage />} />
        <Route path="/notifications/users/:userId" element={<UserInspectorPage />} />
        <Route path="/notifications/alerts" element={<Navigate to="/notifications/alerts/firings" replace />} />
        <Route path="/notifications/alerts/policies" element={<AlertsPoliciesPage />} />
        <Route path="/notifications/alerts/firings" element={<AlertsFiringsPage />} />
        <Route path="/notifications/alerts/firings/:id" element={<FiringDetailPage />} />
        <Route path="/notifications/decisions/:decisionId" element={<DecisionDetailPage />} />
        <Route path="/notifications/audit" element={<AuditPage />} />

        {/* Slice C — Core support */}
        <Route path="/users" element={<UsersListPage />} />
        <Route path="/users/:userId" element={<UserDetailPage />} />
        <Route path="/teams" element={<TeamsListPage />} />
        <Route path="/teams/queue" element={<MatchingQueuePage />} />
        <Route path="/teams/:teamId" element={<TeamDetailPage />} />
        <Route path="/support" element={<SupportListPage />} />
        <Route path="/support/:ticketId" element={<SupportDetailPage />} />

        {/* Slice D — Remaining */}
        <Route path="/communication" element={<CommunicationHomePage />} />
        <Route path="/communication/campaigns/new" element={<CampaignNewPage />} />
        <Route path="/communication/campaigns/:campaignId" element={<CampaignDetailPage />} />
        <Route path="/ai" element={<AiHomePage />} />
        <Route path="/ai/prompts/:promptKey" element={<AiPromptDetailPage />} />
        {/* ADR-022 Session 5 — composable AI funnel observability */}
        <Route path="/ai/coverage-gaps" element={<CoverageGapsPage />} />
        <Route path="/ai/coverage-gaps/:gapId" element={<CoverageGapDetailPage />} />
        <Route path="/ai/outcomes" element={<ApproachOutcomesPage />} />
        <Route path="/ai/portfolio-state" element={<PortfolioStatePage />} />
        <Route path="/ai/user-models" element={<UserModelsPage />} />
        <Route path="/taxonomy" element={<TaxonomyListPage />} />
        <Route path="/taxonomy/:categoryKey" element={<TaxonomyDetailPage />} />
        <Route path="/store" element={<StoreHomePage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/system" element={<SystemPage />} />
        <Route path="/migrations" element={<MigrationsPage />} />

        {/* Dev-only smoke page; removed in Slice E */}
        <Route path="/dev/components" element={<ComponentsShowcase />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
